import { EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import { applyCallFilters } from '../../Queue/EmergencyCallsQueue/utils';

interface CallFilters {
  seatPosition: boolean;
  activeCalls: boolean;
  adminCalls: boolean;
  incidentCalls: boolean;
}

interface Call {
  id?: string;
  isAdmin?: boolean;
  workstationPositions?: string[];
  callStatuses?: string[];
  textFrom911?: {
    data?: {
      isOutboundSMS?: boolean;
    };
  };
  core?: {
    locations?: {
      source?: string;
      class_of_service_category?: string;
    }[];
  };
}

interface CallQueue {
  [callerID: string]: Call;
}

interface CallTakerModeParams {
  activeQueueTab: string;
  hasActiveCallFilters: boolean;
  callQueue: CallQueue;
  callFilters: CallFilters;
  workStation: string;
}

interface MarkerOpacityParams extends CallTakerModeParams {
  shouldMarkerDim: (callerID: string, params: CallTakerModeParams) => boolean;
}

/**
 * Determines if a call should be dimmed based on call taker mode filters
 * @param callerID - The ID of the caller
 * @param params - Parameters for filtering
 * @param params.activeQueueTab - The currently active queue tab
 * @param params.hasActiveCallFilters - Whether there are active call filters
 * @param params.callQueue - The queue of calls
 * @param params.callFilters - The filters to apply
 * @param params.workStation - The current workstation
 * @returns True if the call should be dimmed
 */
export const shouldMarkerDim = (
  callerID: string,
  {
    activeQueueTab,
    hasActiveCallFilters,
    callQueue,
    callFilters,
    workStation,
  }: CallTakerModeParams,
): boolean => {
  // Only apply call taker mode dimming when on 911 calls tab
  if (
    activeQueueTab !== EMERGENCY_CALLS ||
    !hasActiveCallFilters ||
    !callerID
  ) {
    return false;
  }

  const call = callQueue[callerID];
  if (!call) return false;

  // Return true if call should be dimmed (doesn't pass filters)
  return !applyCallFilters(call, callFilters, workStation);
};

/**
 * Calculates the opacity for markers with call taker mode filtering
 * @param incidentType - The type of incident (ALERTS or EMERGENCY_CALLS)
 * @param callerID - The ID of the caller
 * @param baseOpacity - The base opacity value
 * @param params - Parameters for filtering
 * @param params.activeQueueTab - The currently active queue tab
 * @param params.shouldMarkerDim - Function to check if call should be dimmed
 * @returns The calculated opacity value
 */
export const getMarkerOpacity = (
  incidentType: string,
  callerID: string,
  baseOpacity: number,
  params: MarkerOpacityParams,
): number => {
  // For calls, apply call taker mode dimming
  if (incidentType === EMERGENCY_CALLS && callerID) {
    // Apply call taker mode dimming ONLY when on 911 calls tab
    if (
      params.activeQueueTab === EMERGENCY_CALLS &&
      params.shouldMarkerDim(callerID, params)
    ) {
      return baseOpacity * 0.5; // Additional 50% dimming for filtered calls
    }
  }
  return baseOpacity;
};

/**
 * Calculates the color with opacity for map markers based on queue tab and call filters
 * @param callerID - The ID of the caller
 * @param activeQueueTab - The currently active queue tab
 * @param checkShouldDim - Function to check if call should be dimmed
 * @returns RGBA color array [r, g, b, alpha]
 */
export const getMarkerColor = (
  callerID: string,
  activeQueueTab: string,
  checkShouldDim: (callerID: string) => boolean,
): [number, number, number, number] => {
  let opacity = activeQueueTab === EMERGENCY_CALLS ? 1 : 0.2;

  // Apply call taker mode dimming
  const shouldDim = checkShouldDim(callerID);
  if (shouldDim) {
    opacity *= 0.5; // Additional 50% dimming for filtered calls
  }

  // Convert opacity to alpha (0-255) for icon transparency
  const alpha = Math.round(opacity * 255);
  return [255, 255, 255, alpha]; // White color with dynamic alpha
};
